// src/components/Directory/DBCollectorsTable.jsx

import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './DirectoryTable.css';
import API_BASE_URL from '../../config';

const orDash = (val) => {
  if (val === null || val === undefined || val === '' || Number(val) === 0) {
    return '-';
  }
  return val;
};

// Ключ в localStorage для удалённых коллекторов
const STORAGE_HIDE_KEY = 'deletedCollectors';
const ONE_HOUR = 60 * 60 * 1000;

// Ключи в sessionStorage для сохранения состояния сортировки и скролла
const STORAGE_SORT_KEY = 'DBCollectorsTableSort';
const STORAGE_SCROLL_KEY = 'DBCollectorsTableScroll';

const DBCollectorsTable = ({ query, selectedRowId, onRowSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [rowsData, setRowsData] = useState([]); // полный список коллекторов
  const [loading, setLoading] = useState(true);
  const [hideIds, setHideIds] = useState([]); // массив id, которые нужно скрыть

  // Сортировка: инициализируем из sessionStorage, если есть
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_SORT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          key: parsed.key,
          direction: parsed.direction || 'asc',
        };
      }
    } catch {}
    return { key: null, direction: 'asc' };
  });

  // Ссылка на контейнер с прокруткой таблицы
  const tableWrapperRef = useRef(null);

  // ────────────────────────────────────────────────────────────────────────────────
  // 1) Монтируем компонент:
  //    - Запрашиваем всех коллекторов
  //    - Читаем из localStorage удалённые id за последний час
  //    - Восстанавливаем прокрутку таблицы из sessionStorage
  useEffect(() => {
    console.log('[DBCollectorsTable] Загрузка коллекторов, очистка localStorage');
    // 1.a. Загрузка данных
    fetch(`${API_BASE_URL}/collector/get_all_collectors`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => {
        setRowsData(data);
      })
      .catch((err) => console.error('[DBCollectorsTable] Ошибка загрузки:', err))
      .finally(() => setLoading(false));

    // 1.b. Считываем из localStorage список удалённых за последний час
    try {
      const raw = localStorage.getItem(STORAGE_HIDE_KEY);
      const now = Date.now();
      let stored = raw ? JSON.parse(raw) : [];
      // Оставляем только те записи, у которых timestamp < 1 часа
      const valid = stored.filter((item) => now - item.timestamp < ONE_HOUR);
      setHideIds(valid.map((item) => item.id));
      localStorage.setItem(STORAGE_HIDE_KEY, JSON.stringify(valid));
    } catch (e) {
      console.error('[DBCollectorsTable] Ошибка чтения localStorage:', e);
      setHideIds([]);
    }

    // 1.c. Восстановление прокрутки таблицы
    try {
      const savedScroll = sessionStorage.getItem(STORAGE_SCROLL_KEY);
      if (savedScroll && tableWrapperRef.current) {
        tableWrapperRef.current.scrollTop = Number(savedScroll);
      }
    } catch {}
  }, []);

  // ────────────────────────────────────────────────────────────────────────────────
  // 2) Если из CollectorsDetails пришёл deletedId, добавляем его в localStorage и hideIds
  useEffect(() => {
    const deletedId = location.state?.deletedId;
    if (deletedId != null) {
      console.log('[DBCollectorsTable] Скрываем коллектор id =', deletedId);
      const now = Date.now();

      // Читаем текущий массив из localStorage
      let stored = [];
      try {
        const raw = localStorage.getItem(STORAGE_HIDE_KEY);
        stored = raw ? JSON.parse(raw) : [];
      } catch {
        stored = [];
      }

      // Если такого id ещё нет — добавляем { id, timestamp }
      if (!stored.some((item) => item.id === deletedId)) {
        stored.push({ id: deletedId, timestamp: now });
      }

      // Очищаем устаревшие (>1 час) записи
      const valid = stored.filter((item) => now - item.timestamp < ONE_HOUR);

      // Сохраняем обратно и обновляем hideIds
      localStorage.setItem(STORAGE_HIDE_KEY, JSON.stringify(valid));
      setHideIds(valid.map((item) => item.id));

      // Очищаем deletedId из history.state, чтобы при перезагрузке страницы не повторять
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 3) Фильтрация по query (по полям year, name, design.collector_design, material, length)
  const filteredRows = useMemo(() => {
    if (loading) return [];
    const q = (query || '').trim().toLowerCase();
    if (!q) return rowsData;
    return rowsData.filter((r) =>
      [r.year, r.name, r.design?.collector_design, r.material, r.length].some((val) =>
        String(val || '').toLowerCase().includes(q)
      )
    );
  }, [loading, rowsData, query]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 4) Сортировка по выбранному ключу
  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;
    const arr = [...filteredRows].sort((a, b) => {
      let aV = a[sortConfig.key],
        bV = b[sortConfig.key];
      if (['year', 'length'].includes(sortConfig.key)) {
        aV = Number(aV) || 0;
        bV = Number(bV) || 0;
      } else if (sortConfig.key === 'design') {
        // сортируем по design.collector_design
        aV = String(a.design?.collector_design || '').toLowerCase();
        bV = String(b.design?.collector_design || '').toLowerCase();
      } else {
        aV = String(aV || '').toLowerCase();
        bV = String(bV || '').toLowerCase();
      }
      if (aV < bV) return -1;
      if (aV > bV) return 1;
      return 0;
    });
    return sortConfig.direction === 'asc' ? arr : arr.reverse();
  }, [filteredRows, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  // Сохраняем состояние сортировки в sessionStorage при его изменении
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_SORT_KEY, JSON.stringify(sortConfig));
    } catch {}
  }, [sortConfig]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 5) Применяем скрытие: убираем из sortedRows те, чьи id есть в hideIds
  const visibleRows = useMemo(() => {
    if (hideIds.length === 0) return sortedRows;
    return sortedRows.filter((r) => !hideIds.includes(r.id));
  }, [sortedRows, hideIds]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 6) Восстановление прокрутки таблицы (useLayoutEffect для корректного позиционирования до отрисовки)
  useLayoutEffect(() => {
    try {
      const savedScroll = sessionStorage.getItem(STORAGE_SCROLL_KEY);
      if (savedScroll && tableWrapperRef.current) {
        tableWrapperRef.current.scrollTop = Number(savedScroll);
      }
    } catch {}
  }, [visibleRows]);

  // Обработчик прокрутки: сохраняем текущую позицию
  const handleScroll = () => {
    if (tableWrapperRef.current) {
      try {
        sessionStorage.setItem(
          STORAGE_SCROLL_KEY,
          tableWrapperRef.current.scrollTop.toString()
        );
      } catch {}
    }
  };

  // Обработчик клика по строке: сохраняем прокрутку перед навигацией
  const handleRowClick = (row) => {
    if (tableWrapperRef.current) {
      try {
        sessionStorage.setItem(
          STORAGE_SCROLL_KEY,
          tableWrapperRef.current.scrollTop.toString()
        );
      } catch {}
    }
    onRowSelect(row);
  };

  // ────────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="table-container">Загрузка коллекторов…</div>;
  }

  return (
    <div className="table-container">
      <div
        className="table-wrapper"
        ref={tableWrapperRef}
        onScroll={handleScroll}
      >
        <table className="custom-table">
          <colgroup>
            <col style={{ width: '16%' }} />
            <col style={{ width: '50%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th
                className="date-col sortable-header"
                onClick={() => handleSort('year')}
              >
                Год ввода
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSort('name')}
              >
                Название коллектора
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSort('design')}
              >
                Конструктивное решение
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSort('material')}
              >
                Материал
              </th>
              <th
                className="date-col sortable-header"
                onClick={() => handleSort('length')}
              >
                Длина
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr
                key={row.id}
                className={`clickable-row${
                  row.id === selectedRowId ? ' selected-row' : ''
                }`}
                onClick={() => handleRowClick(row)}
              >
                <td className="date-col">{orDash(row.year)}</td>
                <td>{orDash(row.name)}</td>
                <td>{orDash(row.design?.collector_design)}</td>
                <td>{orDash(row.material)}</td>
                <td className="date-col">{orDash(row.length)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DBCollectorsTable;
